import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { apiService, User, LoginRequest, RegisterRequest } from '../services/api';
import BaseApi from '../services/apis/base';
import { setDatadogUser, clearDatadogUser } from '@/services/datadog';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  register: (userData: RegisterRequest) => Promise<void>;
  logout: () => void;
  updateUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const {
    isAuthenticated: auth0Authenticated,
    isLoading: auth0Loading,
    getAccessTokenSilently,
    logout: auth0Logout,
  } = useAuth0();

  // Set up token getter for BaseApi
  useEffect(() => {
    if (auth0Authenticated && !auth0Loading) {
      // Set token getter to use Auth0's getAccessTokenSilently
      BaseApi.setTokenGetter(async () => {
        try {
          return await getAccessTokenSilently();
        } catch (error) {
          console.error('Failed to get Auth0 token:', error);
          return null;
        }
      });
    } else {
      // Clear token getter if not authenticated
      BaseApi.setTokenGetter(null);
    }
  }, [auth0Authenticated, auth0Loading, getAccessTokenSilently]);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // If authenticated with Auth0, get token and sync with backend
        if (auth0Authenticated && !auth0Loading) {
          try {
            const token = await getAccessTokenSilently();
            // Call backend callback to sync user
            const response = await apiService.auth0Callback(token);
            setUser(response.user);
            // Set Datadog user context for error correlation
            setDatadogUser({
              id: response.user.id,
              email: response.user.email,
              name: `${response.user.firstName || ''} ${response.user.lastName || ''}`.trim() || undefined,
            });
          } catch (error) {
            console.error('Failed to sync Auth0 user with backend:', error);
            // If backend sync fails, try to get user from localStorage
            const currentUser = apiService.getCurrentUser();
            if (currentUser) {
              setUser(currentUser);
              // Set Datadog user context for error correlation
              setDatadogUser({
                id: currentUser.id,
                email: currentUser.email,
                name: `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() || undefined,
              });
            }
          }
        } else if (!auth0Loading) {
          // Not authenticated with Auth0, check if we have a local session
          if (apiService.isAuthenticated()) {
            const currentUser = apiService.getCurrentUser();
            if (currentUser) {
              setUser(currentUser);
              // Set Datadog user context for error correlation
              setDatadogUser({
                id: currentUser.id,
                email: currentUser.email,
                name: `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() || undefined,
              });
            } else {
              // Try to fetch fresh user data
              try {
                const userData = await apiService.getProfile();
                setUser(userData);
                // Set Datadog user context for error correlation
                setDatadogUser({
                  id: userData.id,
                  email: userData.email,
                  name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || undefined,
                });
              } catch (error) {
                console.error('Auth check failed:', error);
                // Clear invalid token
                apiService.logout();
              }
            }
          }
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        apiService.logout();
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [auth0Authenticated, auth0Loading, getAccessTokenSilently]);

  const login = async (credentials: LoginRequest) => {
    // Password-based login not supported with Auth0
    throw new Error('Password-based login not supported. Please use Microsoft social login.');
  };

  const register = async (userData: RegisterRequest) => {
    // Password-based registration not supported with Auth0
    throw new Error('Password-based registration not supported. Please use Microsoft social login.');
  };

  const logout = () => {
    apiService.logout();
    setUser(null);
    // Clear Datadog user context
    clearDatadogUser();
    // Logout from Auth0
    auth0Logout({
      logoutParams: {
        returnTo: window.location.origin + '/login',
      },
    });
  };

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user && auth0Authenticated,
    isLoading: isLoading || auth0Loading,
    login,
    register,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
